import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Card from '../common/Card'
import { useTakePlayer } from '../../hooks/useTakePlayer'
import { seedOf, waveformBars } from '../../lib/archive'
import { clock } from '../../lib/clock'
import { COMPARE_BARS, envelopeHeights, sendSteps, sendStepStates, takeEnvelope } from '../../lib/review'

function PlayGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" className="fill-current" aria-hidden="true">
      <path d="M8 5.2v13.6L19 12z" />
    </svg>
  )
}

function PauseGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" className="fill-current" aria-hidden="true">
      <rect x="6" y="4" width="4.4" height="16" rx="1.4" />
      <rect x="13.6" y="4" width="4.4" height="16" rx="1.4" />
    </svg>
  )
}

function CheckGlyph({ size = 16, strokeWidth = 2.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

/** A 52-bar strip; bars up to the playhead take `playedClass`, the rest the idle wave colour. */
function Strip({ heights, progress, playedClass, testId }) {
  return (
    <div dir="ltr" aria-hidden="true" className="flex h-[52px] items-center gap-[2px]" data-strip={testId}>
      {heights.map((height, index) => (
        <span
          key={index}
          className={`flex-1 rounded-[2px] ${progress > 0 && index / COMPARE_BARS <= progress ? playedClass : 'bg-wave-off'}`}
          style={{ height: `${height}px` }}
        />
      ))}
    </div>
  )
}

/**
 * The synchronous wait of the proposal (§5.4), made visible: the upload
 * ticks when the browser has sent the last byte, the assessment is the
 * wait for the answer, the result is ready when it lands. «لغو» aborts
 * the request; the take stays.
 */
function SendingPanel({ phase, onCancel }) {
  const { t } = useTranslation('review')
  const states = sendStepStates(phase)
  return (
    <div className="flex flex-col items-center gap-6 px-3 py-2 text-center" role="status" aria-live="polite">
      <span aria-hidden="true" className="block size-[76px] animate-spin rounded-full border-[3px] border-line border-t-brand-600" />
      <span className="text-lg font-bold text-ink">{t('compare.analyzingTitle')}</span>
      <ul className="w-full space-y-[11px]">
        {sendSteps().map((label, index) => {
          const state = states[index]
          return (
            <li key={label} className="flex items-center gap-2.5" data-step={state}>
              <span
                className={`grid size-5 shrink-0 place-items-center rounded-full text-white ${
                  state === 'done' ? 'bg-good' : state === 'active' ? 'animate-pulse bg-brand-600' : 'bg-track'
                }`}
              >
                {state === 'done' && <CheckGlyph size={11} strokeWidth={3.4} />}
              </span>
              <span className={`text-[13px] ${state === 'todo' ? 'text-ink-3' : state === 'active' ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>{label}</span>
            </li>
          )
        })}
      </ul>
      <button type="button" onClick={onCancel} className="h-11 text-[13.5px] font-semibold text-ink-2 underline underline-offset-4">
        {t('compare.cancel')}
      </button>
    </div>
  )
}

/**
 * The review (design page 7, `review`; P-core-3 of the go-live plan): the
 * reference and the take side by side, each with its strip and its own
 * play button, then «ضبط دوباره» and «ارسال برای تحلیل». The lengths
 * compared and the notes under the buttons went with the owner's «جملات
 * اضافه و توضیحی نمیخوام» (2026-10-14). Playback is single-instance —
 * starting one clip stops the other, as the design says.
 *
 * The take's strip is its real shape (the PCM is in memory); the
 * reference's is seeded from the sentence, as in the archive, because an
 * `<audio>` element hands out no samples — the playhead over it is real.
 */
export default function ReviewCompare({ sentence, reference, take, chip, analyzing, sendPhase, onSubmit, onCancel, onDiscard, onPauseReference }) {
  const { t } = useTranslation('review')
  const player = useTakePlayer(take.url)
  const { pause: pauseTake } = player

  // Single-instance playback: the reference starting stops the take.
  useEffect(() => {
    if (reference.playing) pauseTake()
  }, [reference.playing, pauseTake])

  const referenceHeights = useMemo(() => waveformBars(COMPARE_BARS, seedOf(sentence.referenceAudioUrl ?? sentence.index), COMPARE_BARS - 4), [sentence])
  const takeHeights = useMemo(() => envelopeHeights(takeEnvelope(take.samples)), [take.samples])
  const referenceProgress = reference.seconds > 0 ? Math.min(1, reference.position / reference.seconds) : 0
  const takeProgress = take.seconds > 0 ? Math.min(1, player.position / take.seconds) : 0

  function toggleTake() {
    if (!player.playing) onPauseReference()
    void player.toggle()
  }

  if (analyzing) return <SendingPanel phase={sendPhase} onCancel={onCancel} />

  return (
    <div className="space-y-[15px]">
      <div className="flex justify-center">
        <span className="flex items-center gap-2 rounded-full bg-good-tint px-[15px] py-[9px] text-[13px] font-semibold text-good-ink">
          <CheckGlyph />
          <span dir="rtl">{chip}</span>
        </span>
      </div>

      <Card pad="p-4" className="space-y-3">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="size-[7px] shrink-0 rounded-full bg-ink-2" />
          <span className="flex-1 text-[12.5px] font-bold text-ink-2">{t('compare.referenceAudio')}</span>
          <span dir="ltr" className="tnum text-[11.5px] text-ink-3">
            {clock(reference.position)} / {clock(reference.seconds)}
          </span>
        </div>
        <Strip heights={referenceHeights} progress={referenceProgress} playedClass="bg-ink-2" testId="reference" />
        <button type="button" onClick={reference.onToggle} className="flex h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-surface-2 text-[13px] font-bold text-ink transition hover:bg-surface-3">
          {reference.playing ? <PauseGlyph /> : <PlayGlyph />}
          <span>{reference.playing ? t('compare.stopReference') : t('compare.playReference')}</span>
        </button>
      </Card>

      <section className="space-y-3 rounded-[18px] border-[1.5px] border-brand-600 bg-surface p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="size-[7px] shrink-0 rounded-full bg-brand-600" />
          <span className="flex-1 text-[12.5px] font-bold text-brand-600">{t('compare.myVoice')}</span>
          <span dir="ltr" className="tnum text-[11.5px] text-ink-3">
            {clock(player.position)} / {clock(take.seconds)}
          </span>
        </div>
        <Strip heights={takeHeights} progress={takeProgress} playedClass="bg-brand-600" testId="take" />
        <button type="button" onClick={toggleTake} className="flex h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-[13px] font-bold text-white transition hover:bg-brand-700 dark:text-slate-900">
          {player.playing ? <PauseGlyph /> : <PlayGlyph />}
          <span>{player.playing ? t('compare.stopTake') : t('compare.playTake')}</span>
        </button>
      </section>

      <div className="flex gap-2.5">
        <button type="button" onClick={onDiscard} className="h-[54px] flex-1 rounded-[14px] border-[1.5px] border-line bg-surface text-[14.5px] font-semibold text-ink-2 transition hover:bg-surface-2">
          {t('compare.retake')}
        </button>
        <button type="button" onClick={onSubmit} className="h-[54px] flex-[2] rounded-[14px] bg-brand-600 text-[14.5px] font-semibold text-white shadow-[0_6px_16px_rgba(43,90,150,0.22)] transition hover:bg-brand-700 dark:text-slate-900">
          {t('compare.submit')}
        </button>
      </div>
    </div>
  )
}
